import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { APP_CONFIG, Region } from '../../environment';
import { Observable } from 'rxjs';
import { LocationMapper } from '../location/location.mapper';
import { ICountryEntity } from '../../model/api/location/country.entity.interface';
import { IStateProvinceEntity } from '../../model/api/location/state-province.interface';
import { ICoreLocationRequest } from '../../model/models/location/core-location.location-request.interface';
import { ICountry } from '../../model/models/location/country.interface';
import { IStateProvince } from '../../model/models/location/state-province.interface';
import { ICoreLocation } from '../../model/models/location/core-location.interface';
import { HttpProvider } from '../http/http';
import { ICoreLocationEntity } from '../../model/api/location/core-location.entity.interface';
import { IAgentLocationRequest } from '../../model/models/location/agent-location-request.interface';
import { IAgentLocationResponse } from '../../model/models/location/agent-location.interface';
import { LoggerProvider, LoggerEvent } from '../../providers/logger/logger';
import { AddressLoqate } from '../../model/models/location/addressLoqate.interface';
import { HttpMapper } from '../../providers/myria/http-mapper';
import { ILocationCityRequest } from '../../model/models/location';
import { IAgentCity } from '../../model/models/agent';
import { AgentMapper } from '../../providers/agent/agent.mapper';
import { IAgentCityEntity } from '../../model/api/agent/agent-city.entity.interface';
import { brandings } from '../../environment/branding/brandings';
import { Platform, ActionSheetController } from 'ionic-angular';
import { InAppBrowser } from '@ionic-native/in-app-browser'
import { LaunchNavigator } from "@awesome-cordova-plugins/launch-navigator/ngx"
@Injectable()
export class LocationProvider {
  constructor(private httpProvider: HttpProvider, public http: HttpMapper, private platform: Platform, private actionSheetCtrl: ActionSheetController, private iab: InAppBrowser, private launchNavigator: LaunchNavigator) { }


  /**
  * @description Get the states for a given country.
  */
  getStatesForCountry(isoCode: string): Promise<IStateProvince[]> {
    return this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}country/${isoCode}/states`, {}, (res: IStateProvinceEntity[]) => {
      return LocationMapper.fromStateProvicesEntity(res);
    });
  }

  getStatesListForCountry(isoCode: string): Promise<any[]> {
    return this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}country/${isoCode}/statesList`);
  }

  findLocations(locationRequest: ICoreLocationRequest): Promise<ICoreLocation[]> {
    return this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}agent/locations/locator`, locationRequest,
      (res: ICoreLocationEntity[]) => {
        return LocationMapper.fromCoreAgentLocationEntity(res)
      });
  }

  findAgentLocations(agentLocationRequest: IAgentLocationRequest): Promise<IAgentLocationResponse> {
    try {
      return this.httpProvider.put(`${APP_CONFIG.remittanceApiBaseUrl}location/agent-locations`, agentLocationRequest, (res: IAgentLocationResponse) => {
        LoggerProvider.log(this, null, null, '================AgentLocationResponse===============', res);
        return res;
      });
    }
    catch{ }
  }

  findAllLocations(locationRequest: ICoreLocationRequest, country, isCallAgentLocation: boolean = false): Promise<ICoreLocation[]> {
    return new Promise((resolve) => {
      locationRequest.country = country;
      // setting FindLocationType to null because the old value 'PayNearMe' is not working
      const payNearMeSearchRequest: IAgentLocationRequest = { FindLocationType: null, RequiredReceivingAgents: true, RequiredPayoutAgents: false, RequiredReceivingAndPayoutAgents: false };
      payNearMeSearchRequest.CountryTo = country.toString().toUpperCase();
      payNearMeSearchRequest.RequestCountry = country.toString().toUpperCase();
      payNearMeSearchRequest.Latitude = locationRequest.latitude;
      payNearMeSearchRequest.Longitude = locationRequest.longitude;

      const locationsPromise = this.httpProvider.getAsync(this.findLocations(locationRequest));
      let payNearMeLocationsPromise = null;
      if(APP_CONFIG.featureFlags.kroger && locationRequest.FindLocationType == 'Kroger')
        payNearMeSearchRequest.FindLocationType = locationRequest.FindLocationType
      else if(APP_CONFIG.branding === brandings.Walmart)
        payNearMeSearchRequest.FindLocationType = 'RMT';
      if (locationRequest.sendLocation && !isCallAgentLocation)
        payNearMeLocationsPromise = this.httpProvider.getAsync(this.findAgentLocations(payNearMeSearchRequest));

      this.findAllLocationsAsync(locationsPromise, payNearMeLocationsPromise, resolve, locationRequest, payNearMeSearchRequest);
    });
  }

  async findAllLocationsAsync(locationsPromise, payNearMeLocationsPromise, resolve, locationRequest: any = null, payNearMeSearchRequest: any = null, convert: boolean = true) {
    let locations = [];
    let payNearMeLocationsRaw = [];

    try {
      locations = await this.httpProvider.finishAsync(locationsPromise);
    }
    catch (ex) {
      if (ex.status === 404)
        LoggerProvider.info(this, 'No locations available for search', locationRequest);
      else
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to get locations in store locator page');
    }

    if (payNearMeLocationsPromise) {
      try {
        const payNearMeLocationsResponse = await this.httpProvider.finishAsync(payNearMeLocationsPromise);
        if (payNearMeLocationsResponse)
          payNearMeLocationsRaw = payNearMeLocationsResponse;
      }
      catch (ex) {
        if (ex.status === 404)
          LoggerProvider.info(this, 'No pay near me locations available for search', payNearMeSearchRequest);
        else
          LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to get pay near me locations in store locator page');
      }
    }

    if (locations.length > locationRequest.maxNumAgentsToShow)
      locations.splice(locationRequest.maxNumAgentsToShow);
    if (payNearMeLocationsRaw.length > locationRequest.maxNumAgentsToShow)
      payNearMeLocationsRaw.splice(locationRequest.maxNumAgentsToShow);

    // PayNearMe locations are not available through the locator API so have to get them seperately and them merge them together
    // Since the output from the two different APIs are different, convert can be used so both location types have the same format
    let payNearMeLocations = payNearMeLocationsRaw;
    if (convert)
      payNearMeLocations = this.convertPayNearMeLocations(payNearMeLocationsRaw);
    locations = this.mergeBusinessHours(locations, payNearMeLocations);

    if (resolve)
      resolve(locations);
    return locations;
  }

  convertPayNearMeLocations(locations) {
    const result = []
    for (let i = 0; i < locations.length; i++) {
      const location = locations[i];
      result.push({
        agentId: location.agentToId,
        agentLocId: location.locationId,
        agentNo: location.agentToId,
        name: location.name,
        agentLocName: location.agentToName,
        distance: (APP_CONFIG.region.distanceMetric === 'km') ? location.distanceFromOriginKilometers : location.distanceFromOriginMiles,
        sendLocation: location.sendLocation,
        payoutLocation: location.payoutLocation,
        businessHours: location.businessHours,
        collectLocation: location.collectionLocation,
        currencyExchangeLocation: false,
        ownedByCompany: false,
        brandIconKey: null,
        agentTelephone: location.phone,
        locationDisplayName: location.name,
        agentName: location.name,
        acceptCash: false,
        address: {
          street: location.address ? location.address : location.locationAddress + (location.locationAddress2 ? ', ' + location.locationAddress2 : ''),
          postalCode: null,
          city: location.city ? location.city : location.locationCity,
          country: null,
          latitude: location.latitude,
          longitude: location.longitude
        },
        selected: false,
        payNearMeLocation: true
      });
    }
    return result;
  }

  compareLocations(a, b) {
    if (a.distance) {
      if (a.distance > b.distance) return 1;
      if (b.distance > a.distance) return -1;
    }
    else if (a.distanceFromOriginMiles) {
      if (a.distanceFromOriginMiles > b.distanceFromOriginMiles) return 1;
      if (b.distanceFromOriginMiles > a.distanceFromOriginMiles) return -1;
    }

    return 0;
  }

  mergeBusinessHours(locations, payNearMeLocactions) {
    if(payNearMeLocactions.length == 0) {
      return locations
    }
    const sorted = {}
    for (let i = 0; i < payNearMeLocactions.length; i++) {
      const payNearMeLocaction = payNearMeLocactions[i];
      sorted[payNearMeLocaction.agentLocId] = payNearMeLocaction;
    }
    for (let i = 0; i < locations.length; i++) {
      const location = locations[i];
      if (location.agentLocId > 0) {
        const payNearMeLocaction = sorted[location.agentLocId];
        if (payNearMeLocaction) {
          location.businessHours = payNearMeLocaction.businessHours
          location.distance = payNearMeLocaction.distance
        }
      }
    }
    return locations;
  }

  mergeLocations(locations, payNearMeLocactions, count = 20) {
    locations = locations.concat(payNearMeLocactions);
    locations.sort(this.compareLocations);
    if (locations.length > count)
      locations.splice(count);
    return locations;
  }

  getLoqateAddress(address: string, searchId?: string): Promise<AddressLoqate[]> {
    let params = 'address=' + address + (searchId ? '&id=' + searchId : '');
    params = encodeURI(params);
    return this.http.get(APP_CONFIG.API_URL + 'api/Addresses/Find?' + params).toPromise<AddressLoqate[]>();
  }

  getLoqateAddressValues(addressId: string): Promise<any> {
    addressId = encodeURI(addressId);
    return this.http.get(APP_CONFIG.API_URL + 'api/Addresses/Find/Fields?id=' + addressId).toPromise();
  }

  getCountryCities(request: ILocationCityRequest) : Promise<IAgentCity[]> {
    return this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}locations/cities`, request, (res: IAgentCityEntity[]) => {
      return AgentMapper.fromAgentCitiesEntity(res);
    });
  }

  async openMapWithDirection(addressString) {
    if (this.platform.is('ios')) {
      let isGoogleAppAvailable = null;
      let buttons = [
        {
          text: "Maps", handler: () => {
            const url = `https://maps.apple.com/?q=${this.encodeIOS(addressString)}`;
            if (window.location) window.location.href = url;
          }
        }];
      try {
        isGoogleAppAvailable = await this.launchNavigator.isAppAvailable(this.launchNavigator.APP.GOOGLE_MAPS);
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, "Unable to check if app is installed for direction");
      }
      if(isGoogleAppAvailable) {
        buttons.push({
          text: "Google Maps", handler: () => {
            const url = `https://www.google.com/maps/search/${encodeURI(addressString)}`;

            if (window.location) window.location.href = url;
          }
        });
      }

      this.actionSheetCtrl.create({
        buttons: buttons
      }).present();
    } else {
      this.actionSheetCtrl.create({
        buttons: [
          { text: "Google Maps", handler: () => { this.iab.create(`https://www.google.com/maps/search/${encodeURI(addressString)}`, '_system')}}
        ]
      }).present();
    }
  }

  encodeIOS(value) {
    return value.replace(' ', '+');
  }
}



// WEBPACK FOOTER //
// ./src/providers/location/location.ts